import { useEffect } from 'react';
import { create } from 'zustand';

import { tokenExpiresWithin } from '../boot/session';
import { refreshSession } from './http';

/**
 * The builder's BACKGROUND upload registry (founder, 2026-09-15: "si je suis en train de télécharger une
 * image, et je ferme le composant elle est perdue — même si je ferme, le téléchargement doit continuer en
 * arrière-plan").
 *
 * WHY THIS EXISTS. `ImageControl` / `VideoControl` used to own the whole upload in component state:
 * the object URL, the percentage, the abort controller, the error. The bytes themselves survived the
 * panel closing (an XHR is not tied to the DOM), but EVERYTHING THE SELLER CAN SEE died with the
 * component, and two things were genuinely lost:
 *   - a failure after the panel closed set state on an unmounted component, i.e. NOTHING. No message,
 *     no image, no trace — the upload simply evaporated;
 *   - re-opening the panel mid-flight showed an empty drop zone, so the seller re-picked the same file
 *     and a SECOND upload raced the first for the same field, last writer winning at random.
 *
 * So the job moves here: a module-level store, addressed by TARGET (`block:<sectionId>:<blockId>#<fieldId>`),
 * outliving every mount. A control that mounts on a key with a job in flight re-attaches to it — it shows
 * the same picture, the same percentage, the same Cancel — instead of starting again. The topbar shows the
 * count while no panel is open, and a job that fails is KEPT here, with its file, so the seller is told and
 * can retry the bytes we already read.
 *
 * The promise returned by `run` is what writes the result into the document; it must therefore close over
 * store actions (stable) rather than component state — see the controls.
 */

export type UploadKind = 'image' | 'video';

/** `sending` = bytes leaving; `processing` = last byte gone, the server is transcoding; `error` = kept for the seller. */
export type UploadPhase = 'sending' | 'processing' | 'error';

export interface MediaUpload {
  key: string;
  kind: UploadKind;
  /** Name of the picked file — what the topbar and the error line call it. */
  fileName: string;
  /** `blob:` URL of the seller's own file, previewed while the bytes travel. Revoked by the registry. */
  previewUrl?: string;
  percent: number;
  phase: UploadPhase;
  /** Attempt number (2, 3, …) while a dropped connection is being re-sent; 0 on the first try. */
  retrying: number;
  /** Set with `phase: 'error'` — the reason, in the seller's language, as the uploader phrased it. */
  error?: string;
  /**
   * Has the transport reported ANY progress yet?
   *
   * `percent: 0` is ambiguous on its own — it means both "nothing has left" and "nobody has told us
   * anything", and those need different pictures. `xhr.upload.onprogress` is not guaranteed: it does not
   * fire at all for some bodies (a tiny multipart, or an intercepted request — our own e2e harness caught
   * this), and when it does the first event can be late. Rendering a determinate "0 %" on that silence is
   * the same class of lie as the "100 %" this work exists to remove: a number presented as measurement
   * when nothing has been measured. Until this flips, the bar says "working" without claiming a figure.
   */
  reported: boolean;
  /**
   * `Date.now()` when the seller picked the file — what the UI counts from.
   *
   * It exists because the percentage cannot carry the whole story: `xhr.upload.onprogress` reports bytes
   * handed to the SOCKET, so on a small file the bar fills in one jump and everything after it looks
   * frozen. Elapsed time is the one signal that is always true and always moving.
   */
  startedAt: number;
  /** Stop the job. A cancelled job leaves no trace (it was deliberate). */
  cancel: () => void;
  /** Re-send the SAME bytes. Present on a failed job only. */
  retry: () => void;
  /** Bring the inspector back to the field this job belongs to, so a failure is actionable from the topbar. */
  reveal?: () => void;
}

interface MediaUploadState {
  uploads: Record<string, MediaUpload>;
  /**
   * Keys whose job is ALREADY on screen in an open panel, counted by mounted control.
   *
   * Founder, 2026-09-16: "pas besoin d'écrire la sauvegarde dans le header puisqu'on l'écrit déjà dans
   * l'image qui se télécharge." Her screenshot has the same sentence twice — "Sending hf_…png… 94 % 11 s"
   * in the topbar and, two centimetres below, the bar drawn over the picture itself. The topbar line was
   * never meant for that moment: it exists for the upload whose panel is CLOSED, which otherwise has
   * nothing on screen at all. So a control that renders the job takes the topbar's turn away while it is
   * mounted, and hands it back the moment it goes.
   */
  shown: Record<string, number>;
}

export const useMediaUploads = create<MediaUploadState>(() => ({ uploads: {}, shown: {} }));

/**
 * The last slice of the bar, reserved for the round trip — so a FULL bar can only ever mean "landed".
 *
 * Founder, 2026-09-15: her screenshot shows "Envoi de 4.png… 100 %" over a frozen bar and the word
 * "Finalisation…", for minutes. Nothing was broken — `xhr.upload.onprogress` counts bytes handed to the
 * SOCKET, not bytes received, and on a body of a few hundred KB the kernel swallows the lot in one go.
 * So the bar reported "done" before the file had begun to climb her uplink, and the entire real transfer
 * happened behind a number that said there was nothing left to wait for.
 *
 * Reserving the tail makes the number honest again: an upload is complete when the SERVER says so, and
 * until then the bar must not claim it is. 100 % is now unreachable by construction — a landed job is
 * removed from the registry, so the seller never sees it at all.
 */
const SENT_CAP = 99;

/** Socket progress (0-100) → what the seller is shown (0-[SENT_CAP]). */
export function displayedPercent(socketPercent: number): number {
  const clamped = Math.max(0, Math.min(100, socketPercent));
  return Math.min(SENT_CAP, Math.round((clamped * SENT_CAP) / 100));
}

function patch(key: string, next: Partial<MediaUpload>): void {
  useMediaUploads.setState((s) => {
    const cur = s.uploads[key];
    if (!cur) return s;
    return { uploads: { ...s.uploads, [key]: { ...cur, ...next } } };
  });
}

/** Drop the job and release the blob URL it was pinning (a 30 MB clip stays in memory until this runs). */
function forget(key: string): void {
  const job = useMediaUploads.getState().uploads[key];
  if (!job) return;
  if (job.previewUrl) URL.revokeObjectURL(job.previewUrl);
  useMediaUploads.setState((s) => {
    const rest = { ...s.uploads };
    delete rest[key];
    return { uploads: rest };
  });
}

/** Everything the running job may report back. Handed to `run`, valid for that job only. */
export interface UploadHandle {
  signal: AbortSignal;
  setPercent: (percent: number) => void;
  setPhase: (phase: 'sending' | 'processing') => void;
  setRetrying: (attempt: number) => void;
  /**
   * Hand a freshly created object URL to the registry. From that call on the registry owns it and will
   * revoke it when the job ends — including when the panel that created it is long gone.
   */
  ownPreview: (url: string) => void;
  /**
   * Keep the local blob ALIVE past the end of the job, filed under the delivery URL it was uploaded to.
   *
   * For video this is the difference between an instant preview and re-downloading, from the object store,
   * the 20 MB clip that is already in this tab's memory. The control asks for it back with
   * `localPreviewFor(value.sources[0].url)`, which now works from ANY panel — including a panel remounted
   * after the seller closed and re-opened it, which the old component-owned blob could never survive.
   */
  keepPreviewFor: (deliveryUrl: string) => void;
}

/**
 * Local blobs kept after their upload succeeded, keyed by the delivery URL they became. A blob pins the
 * whole file in memory, so the map is small and strictly FIFO — three clips is generous for one session
 * of editing, and the oldest is revoked the moment a fourth arrives.
 */
const KEEP_MAX = 3;
const kept = new Map<string, string>();

function keepLocalPreview(deliveryUrl: string, blobUrl: string): void {
  const previous = kept.get(deliveryUrl);
  if (previous && previous !== blobUrl) URL.revokeObjectURL(previous);
  kept.delete(deliveryUrl);
  kept.set(deliveryUrl, blobUrl);
  while (kept.size > KEEP_MAX) {
    const oldest = kept.keys().next().value as string;
    const url = kept.get(oldest);
    kept.delete(oldest);
    if (url) URL.revokeObjectURL(url);
  }
}

/** The local blob for a stored asset, if this tab is the one that uploaded it and still holds the bytes. */
export function localPreviewFor(deliveryUrl: string | undefined): string | undefined {
  return deliveryUrl ? kept.get(deliveryUrl) : undefined;
}

export interface StartUploadOptions {
  /** Target identity — `<field target>#<field id>`. One job per field; a new pick replaces the old one. */
  key: string;
  kind: UploadKind;
  fileName: string;
  reveal?: () => void;
  /** The whole job: upload, then WRITE THE RESULT into the document. Runs to completion with no mounted component. */
  run: (handle: UploadHandle) => Promise<void>;
  /** Turn a thrown value into the line the seller reads. */
  describeError: (e: unknown) => string;
  /**
   * Undo the OPTIMISTIC value `run` wrote into the document — the seller's own `blob:` file, painted in the
   * canvas the instant they picked it so they never wait on their uplink to see their own picture.
   *
   * It belongs here rather than in a `catch` inside `run` because of one collision: "Remove" both cancels
   * the job AND sets the field itself. Reverting asynchronously, from the job's rejection, would land AFTER
   * that and resurrect the picture the seller just removed. Called from [cancel] it is SYNCHRONOUS, so
   * "Remove" simply overwrites it a line later and the field ends up empty, which is what was asked.
   *
   * Runs at most once per attempt, on the two endings that leave nothing behind: the seller cancelling, and
   * a failure. Never on success.
   */
  revert?: () => void;
}

/**
 * Start (or replace) the upload for one field, and let it finish on its own.
 *
 * Replacing matters: picking a second file for a field that is already uploading used to leave two jobs
 * racing for the same setting. Here the previous one is ABORTED first, so the field has exactly one writer.
 */
export function startMediaUpload(opts: StartUploadOptions): void {
  const { key } = opts;
  const running = useMediaUploads.getState().uploads[key];
  if (running) {
    running.cancel();
    forget(key);
  }

  const ctrl = new AbortController();
  // One undo per attempt: cancelling already reverted, so the rejection that follows must not revert
  // again — a second one, landing after "Remove" set the field, would put the old picture back.
  let reverted = false;
  const revertOnce = () => {
    if (reverted) return;
    reverted = true;
    opts.revert?.();
  };
  const job: MediaUpload = {
    key,
    kind: opts.kind,
    fileName: opts.fileName,
    percent: 0,
    phase: 'sending',
    retrying: 0,
    reported: false,
    startedAt: Date.now(),
    // Synchronous on purpose — see `revert` in StartUploadOptions.
    cancel: () => {
      revertOnce();
      ctrl.abort();
    },
    retry: () => startMediaUpload(opts),
    reveal: opts.reveal,
  };
  useMediaUploads.setState((s) => ({ uploads: { ...s.uploads, [key]: job } }));

  const handle: UploadHandle = {
    signal: ctrl.signal,
    setPercent: (percent) =>
      patch(key, {
        percent: displayedPercent(percent),
        reported: true,
        phase: percent >= 100 ? 'processing' : 'sending',
      }),
    setPhase: (phase) => patch(key, { phase }),
    setRetrying: (retrying) => patch(key, { retrying, reported: false, percent: 0 }),
    ownPreview: (url) => {
      const cur = useMediaUploads.getState().uploads[key];
      // A retry creates a second blob for the same job; release the one we are replacing.
      if (cur?.previewUrl && cur.previewUrl !== url) URL.revokeObjectURL(cur.previewUrl);
      patch(key, { previewUrl: url });
    },
    keepPreviewFor: (deliveryUrl) => {
      const url = useMediaUploads.getState().uploads[key]?.previewUrl;
      if (!url) return;
      keepLocalPreview(deliveryUrl, url);
      // Ownership has moved to the keepsake map — `forget` must not revoke it out from under the preview.
      patch(key, { previewUrl: undefined });
    },
  };

  void withFreshSession(ctrl.signal)
    .then(() => opts.run(handle))
    .then(() => forget(key))
    .catch((e: unknown) => {
      // Cancelling is a deliberate act, not a failure — it leaves nothing behind to explain. The
      // optimistic value was already undone, synchronously, by `cancel`.
      if (e instanceof DOMException && e.name === 'AbortError') {
        forget(key);
        return;
      }
      // The optimistic value promised a picture this upload did not deliver. Take it back before showing
      // the message, so the canvas never keeps a `blob:` that dies with the tab.
      revertOnce();
      // KEPT on purpose: this is the case that used to vanish silently when the panel was closed. The
      // job stays in the registry with its message so the topbar can say it and the panel shows it on
      // re-open, with the bytes still in hand for "Try again".
      patch(key, { phase: 'error', error: opts.describeError(e), retrying: 0 });
    });
}

/**
 * How much life an access token must have left for an upload to be started on it.
 *
 * Ten minutes covers a 30 MB clip on a poor uplink (measured ~2.5 min at 194 KB/s, and this seller's line
 * has been seen 30× slower than that). Over-refreshing costs one cheap POST that the single-flight in
 * `refreshSession` already coalesces; under-refreshing costs the seller their entire file, twice.
 */
const FRESH_ENOUGH_MS = 10 * 60 * 1000;

/**
 * Make sure the session will outlive the upload, BEFORE the bytes leave.
 *
 * Without this, a token expiring mid-upload is answered by `withAuthRetry` the only way it can be —
 * refresh, then send the request again. For an API call that is invisible and right. For an upload it
 * means the whole file goes up a SECOND time, and nothing says so: the progress bar is already at its
 * cap, the panel already says "Finishing up…", and the seller simply waits twice as long for no reason
 * they can see.
 *
 * Never fatal: if the refresh fails we still run, and the 401 replay remains the backstop — an upload
 * must not be blocked because a pre-flight was unlucky.
 */
async function withFreshSession(signal: AbortSignal): Promise<void> {
  if (signal.aborted || !tokenExpiresWithin(FRESH_ENOUGH_MS)) return;
  try {
    await refreshSession();
  } catch {
    /* the upload goes ahead on the token we have; a 401 is still handled downstream */
  }
}

/** Drop a FAILED job the seller has acknowledged (clearing the field, or dismissing the message). */
export function dismissMediaUpload(key: string): void {
  forget(key);
}

/** Abort whatever is running for this field and forget it — used when the seller removes the media. */
export function cancelMediaUpload(key: string): void {
  const job = useMediaUploads.getState().uploads[key];
  if (!job) return;
  job.cancel();
  forget(key);
}

/**
 * The job for one field, or undefined. Re-renders the control on every percentage tick.
 *
 * Calling this also DECLARES that the caller is painting the job (every caller is a media control that
 * draws the bar over the picture). That is what keeps the topbar quiet while the panel is open — see
 * `shown` above; a control cannot forget to say so, because asking for the job is saying so.
 */
export function useMediaUpload(key: string): MediaUpload | undefined {
  useEffect(() => {
    useMediaUploads.setState((s) => ({ shown: { ...s.shown, [key]: (s.shown[key] ?? 0) + 1 } }));
    return () =>
      useMediaUploads.setState((s) => {
        const next = (s.shown[key] ?? 1) - 1;
        const shown = { ...s.shown };
        if (next > 0) shown[key] = next;
        else delete shown[key];
        return { shown };
      });
  }, [key]);
  return useMediaUploads((s) => s.uploads[key]);
}

/** Every job, in insertion order — what the topbar indicator reads. */
export function useMediaUploadList(): MediaUpload[] {
  const uploads = useMediaUploads((s) => s.uploads);
  return Object.values(uploads);
}

/** Which jobs a panel is currently showing by itself — the topbar leaves those to it. */
export function useMediaUploadsShown(): Record<string, number> {
  return useMediaUploads((s) => s.shown);
}

/** Tests only: no job may survive into the next spec. */
export function __resetMediaUploads(): void {
  for (const key of Object.keys(useMediaUploads.getState().uploads)) forget(key);
  useMediaUploads.setState({ shown: {} });
  for (const url of kept.values()) URL.revokeObjectURL(url);
  kept.clear();
}
